import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { assignPrefIdList } from '@doda/common/src/logics/masterCache';
import { CompanyOfSearchModal } from '@doda/common/src/models/MasterCacheModel';
import { getRailwayCompany } from '@doda/common/src/services/master/getRailwayCompanyAPI';
import { Dispatch, SetStateAction, useReducer, useState } from 'react';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { createDisplayArea } from 'components/LocationStationModal/LocationModal/logics/createLocationDisplayData';
import {
  AreaResponse,
  JobCountResponse,
  RailwayCompanyResponse,
} from '@doda/api-client/aspida/@types';
import { includeStationChildIds } from '../../logics/organizeStationIds';
import {
  initialState,
  reducer,
  ReducerAction,
} from '../../logics/checkedStationStateReducer';

/**
 * 路線・駅モーダルのカスタムフック
 * @param params.selectedCurrentCondition 選択済み検索条件
 * @param params.locationAndStationModalJobCount エリア、路線駅の求人件数
 * @param params.areaMaster エリアマスタ
 * @param params.onRailwayMasterFetch 路線・駅マスタ取得時の実行処理
 * @returns 路線・駅モーダル関連パラメータ
 */
export const useStationModal = ({
  selectedCurrentCondition,
  locationAndStationModalJobCount,
  areaMaster,
  onRailwayMasterFetch,
}: {
  selectedCurrentCondition: SelectedCurrentConditionType;
  locationAndStationModalJobCount: JobCountResponse;
  areaMaster: AreaResponse[];
  onRailwayMasterFetch?: (data: RailwayCompanyResponse[]) => void;
}) => {
  const stationModal = useDisclosure();

  // 路線・駅マスタの状態管理
  const [routeMaster, setRouteMaster] = useState<CompanyOfSearchModal[]>([]);

  // 路線・駅マスタ取得処理
  const getRouteMasterProcess = async () => {
    const routeData = await getRouteMaster({
      routeMaster,
      setRouteMaster,
      onRailwayMasterFetch,
    });
    initCheckedStation({
      selectedCurrentCondition,
      routeMaster: routeData,
      checkedStationDispatch,
    });
  };

  // 路線・駅モーダル表示
  const onOpenRouteModal = () => {
    // モーダルを表示
    stationModal.onOpen();
    getRouteMasterProcess();
  };

  // 路線マスタ リロード
  const reloadRouteMaster = async () => {
    await getRouteMasterProcess();
  };

  // 路線駅のチェック済・選択済状態管理
  const [checkedStationState, checkedStationDispatch] = useReducer(reducer, {
    ...initialState,
    areas: createDisplayArea(areaMaster, locationAndStationModalJobCount),
  });

  return {
    stationModal,
    routeMaster,
    onOpenRouteModal,
    reloadRouteMaster,
    checkedStationState,
    checkedStationDispatch,
  };
};

/**
 * 路線マスタ取得
 * @param params.routeMaster 路線マスタのState
 * @param params.setRouteMaster 路線マスタSetter
 * @param params.onRailwayMasterFetch 路線・駅マスタ取得時の実行処理
 * @returns 路線マスタ
 */
const getRouteMaster = async ({
  routeMaster,
  setRouteMaster,
  onRailwayMasterFetch,
}: {
  routeMaster: CompanyOfSearchModal[];
  setRouteMaster: Dispatch<SetStateAction<CompanyOfSearchModal[]>>;
  onRailwayMasterFetch?: (data: RailwayCompanyResponse[]) => void;
}) => {
  // マスタデータ取得済みの場合
  if (routeMaster.length) return routeMaster;

  // APIでマスタ取得
  const responseData = await getRailwayCompany();

  if (onRailwayMasterFetch) onRailwayMasterFetch(responseData);
  const routeData = assignPrefIdList(responseData);
  setRouteMaster(routeData);
  return routeData;
};

/**
 * 路線・駅のチェック状態を初期化
 * @param params.selectedCurrentCondition 選択済み検索条件
 * @param params.routeMaster 路線・駅マスタ
 * @param params.checkedStationDispatch チェック状態dispatch
 */
const initCheckedStation = ({
  selectedCurrentCondition,
  routeMaster,
  checkedStationDispatch,
}: {
  selectedCurrentCondition: SelectedCurrentConditionType;
  routeMaster: CompanyOfSearchModal[];
  checkedStationDispatch: Dispatch<ReducerAction>;
}) => {
  const updatedData = includeStationChildIds(routeMaster, {
    checkedAllRoute: selectedCurrentCondition.routeId,
    checkedStation: selectedCurrentCondition.stationId,
  });

  checkedStationDispatch({
    type: 'update',
    payload: updatedData,
  });
  checkedStationDispatch({
    type: 'init',
    master: routeMaster,
  });
};
